import { useState } from 'react';
import { Link } from 'react-router';
import type { ProgramRow } from '@st/contract';
import { useCreateProgram, useDeleteProgram, useTrainerPrograms } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { formatDate } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Input,
  Modal,
  SkeletonList,
  Textarea,
  useToast,
} from '../../ui';

export function TrainerPrograms() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const query = useTrainerPrograms();
  const create = useCreateProgram();
  const remove = useDeleteProgram();

  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProgramRow | null>(null);

  function onDelete(program: ProgramRow) {
    remove.mutate(program.id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast(t('builder.deleted'), 'success');
      },
      onError: (error) => toast(toMutationMessage(t, error), 'danger'),
    });
  }

  return (
    <>
      <PageHeader
        eyebrow={t('shell.trainer')}
        title={t('shell.programs')}
        action={
          <Button size="sm" onClick={() => setCreating(true)}>
            {t('trainer.newProgram')}
          </Button>
        }
      />

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={3} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('empty.noPrograms'),
          actionLabel: t('trainer.newProgram'),
          onAction: () => setCreating(true),
        }}
      >
        {(data) => (
          <ul className="grid gap-4 md:grid-cols-2">
            {data.items.map((p) => (
              <Card as="li" key={p.id} className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-display text-[21px] font-semibold leading-tight text-ink">
                      {p.nameHr}
                    </h2>
                    {p.descriptionHr && (
                      <p className="mt-1 text-[13px] text-ink-muted">{p.descriptionHr}</p>
                    )}
                  </div>
                  {p.isTemplate && <Badge tone="accent">{t('builder.isTemplate')}</Badge>}
                </div>

                <p className="text-[12px] text-ink-muted">
                  {t('builder.weeksShort', { n: p.weeksCount })} · {formatDate(p.updatedAt, locale)}
                </p>

                <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                  <Link
                    to={`/trener/programi/${p.id}`}
                    className="inline-flex items-center rounded-pill border border-line-strong px-4 py-2 text-[13px] text-ink transition-colors duration-200 hover:bg-bg-alt"
                  >
                    {t('builder.openBuilder')}
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => setPendingDelete(p)}>
                    {t('action.delete')}
                  </Button>
                </div>
              </Card>
            ))}
          </ul>
        )}
      </QueryBoundary>

      <NewProgramModal
        open={creating}
        onClose={() => setCreating(false)}
        pending={create.isPending}
        onSubmit={(input) =>
          create.mutate(input, {
            onSuccess: () => {
              setCreating(false);
              toast(t('builder.created'), 'success');
            },
            onError: (error) => toast(toMutationMessage(t, error), 'danger'),
          })
        }
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('builder.deleteProgram')}
        question={t('builder.deleteProgramConfirm', { name: pendingDelete?.nameHr ?? '' })}
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('a11y.closeDialog')}
        loading={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && onDelete(pendingDelete)}
      />
    </>
  );
}

interface NewProgramValues {
  nameHr: string;
  descriptionHr: string | null;
  isTemplate: boolean;
}

function NewProgramModal({
  open,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: NewProgramValues) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isTemplate, setIsTemplate] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  function submit() {
    if (name.trim().length < 2) {
      setError(t('builder.nameRequired'));
      return;
    }
    setError(undefined);
    onSubmit({
      nameHr: name.trim(),
      descriptionHr: description.trim() || null,
      isTemplate,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('builder.newProgramTitle')}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('action.create')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          id="program-name"
          label={t('builder.programName')}
          value={name}
          required
          error={error}
          onChange={(e) => setName(e.target.value)}
        />
        <Textarea
          id="program-description"
          label={t('builder.programDescription')}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Checkbox
          id="program-template"
          label={t('builder.isTemplate')}
          checked={isTemplate}
          onChange={(e) => setIsTemplate(e.target.checked)}
        />
        <p className="text-[12px] text-ink-muted">{t('builder.isTemplateHelp')}</p>
      </form>
    </Modal>
  );
}
